<template>
  <div>
    <SidebarSettingsGroup
      title="Frame URL"
      collapsible
      :element-type="element.type"
      data-test="graphics-frameSettings-general"
      :tooltip="true"
      tooltip-label="Not all websites support running within an IFrame."
      tooltip-pos="down"
    >
      <div class="sidebar-form-group">
        <div
          class="flex margin-bottom-sm"
          data-test="graphics-qrCodeSettings-permalink"
        >
          <validation-field as="input"
            v-model="url"
            rules="required"
            :class="{ 'is-invalid': validationErrors['url'] || (url && !isValidUrl) }"
            type="text"
            placeholder="https://example.com"
            name="url"
            data-test="graphics-frame-setUrl"
            data-entity="graphics-frame-setUrl"
            :disabled="isExperience"
          />
        </div>
        <button
          type="button"
          class="btn btn-style-b btn-sm"
          data-test="graphics-frame-refresh"
          data-entity="graphics-frame-refresh"
          @click="refreshFrame"
        >
          <span class="far fa-sync margin-right-xs" />
          Refresh
        </button>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-frameSettings-styleAndAnimation"
    >
      <template>
        <opacity-settings v-model="opacity" />
        <hr class="sidebar-divider" />
      </template>

      <div class="sidebar-form-group">
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">Background color</label>
        <label
          class="radio inline-radio"
          for="solid"
        >
          Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-frameSettings-fillStyleSolid"
            data-entity="graphics-frameSettings-fillStyleSolid"
          >
          <div class="checkmark" />
        </label>

        <label
          class="radio inline-radio"
          for="gradient"
        >
          Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-frameSettings-fillStyleGradient"
            data-entity="graphics-frameSettings-fillStyleGradient"
          >
          <div class="checkmark" />
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker text-center"
          data-test="graphics-frameSettings-fillStyleSolidColor"
        >
          <colorpicker
            v-model="backgroundColor"
            :is-mapped="checkMapped('backgroundColor')"
          />

          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('backgroundColor')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>

        <div
          v-else
          class="settings-row settings-row-xs"
          data-test="graphics-frameSettings-fillStyleGradient"
        >
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="gradientDirection"
                rules="required|numeric"
                type="number"
                name="gradientDirection"
                :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
                data-test="graphics-frameSettings-setGradientDirection"
                data-entity="graphics-frameSettings-setGradientDirection"
              />
              <span class="input-group-addon symbol">°</span>
            </div>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorOne"
              :is-mapped="checkMapped('gradientColorOne')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down"
              data-balloon-length="medium"
            >
              <span
                v-if="checkMapped('gradientColorOne')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>
          <div class="settings-col-3 settings-col-xs colorpicker text-center">
            <colorpicker
              v-model="gradientColorTwo"
              :is-mapped="checkMapped('gradientColorTwo')"
            />
            <span
              data-balloon="This setting is controlled by data. Unmap data to customize."
              data-balloon-pos="down-right"
              data-balloon-length="medium"
            >
              <span
                v-if="checkMapped('gradientColorTwo')"
                class="fa fa-lock mapped-lock"
              />
            </span>
          </div>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <background-image-settings
        :element="element"
        :scroll-offset="scrollOffset"
        @modal="(opts) => $emit('modal', opts)"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-frameSettings-toggleBorder"
            data-entity="graphics-frameSettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <validation-field as="input"
                v-model.number="borderWidth"
                rules="required|numeric|min:0"
                type="number"
                name="borderWidth"
                min="0"
                :class="{ 'is-invalid': validationErrors['borderWidth'] }"
                data-test="graphics-frameSettings-setBorderWidth"
                data-entity="graphics-frameSettings-setBorderWidth"
              />
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker
                v-model="borderColor"
                :is-mapped="checkMapped('borderColor')"
              />
              <label class="sidebar-input-sub-label">
                Color
                <span
                  data-balloon="This setting is controlled by data. Unmap data to customize."
                  data-balloon-pos="down-right"
                  data-balloon-length="medium"
                >
                  <span
                    v-if="checkMapped('borderColor')"
                    class="fa fa-lock mapped-lock"
                  />
                </span>
              </label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <box-shadow-settings
        :element="element"
        :data-provider="dataProvider"
        :scroll-offset="scrollOffset"
      />

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-frameSettings-toggleBlur"
            data-entity="graphics-frameSettings-toggleBlur"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <div
          v-if="blur"
          class="input-group"
          data-test="graphics-frameSettings-setBlurSize"
        >
          <validation-field as="input"
            v-model.number="blurSize"
            rules="required|numeric|min:0"
            type="number"
            name="blurSize"
            min="0"
            :class="{ 'is-invalid': validationErrors['blurSize'] }"
            data-test="graphics-frameSettings-setBlurSize"
            data-entity="graphics-frameSettings-setBlurSize"
          />
          <span class="input-group-addon">px</span>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings :element="element"></animation-settings>
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';
import { useFormContext } from 'vee-validate';
import EventBus from '../../utils/event-bus';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import BackgroundImageSettings from './BackgroundImage.vue';
import BorderRadius from './BorderRadius.vue';

export default {
  emits: ['modal'],
  name: 'RectangleSettingsComponent',
  setup() {
    const { validateField } = useFormContext();
    return { validateFormField: validateField };
  },

  components: {
    AnimationSettings,
    OpacitySettings,
    BoxShadowSettings,
    BackgroundImageSettings,
    BorderRadius,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },

    scrollOffset: {
      type: Number,
      default: 0,
    },
  },

  methods: {
    refreshFrame() {
      EventBus.emit('frame:refresh', { id: this.element.id });
    },
  },

  computed: {
    isExperience() {
      return !!this.getPropertyValue('experienceId');
    },

    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },

      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    url: {
      get() {
        return this.getPropertyValue('url');
      },

      set(url) {
        this.updateOneElementValue('url', url);
        this.$nextTick(() => this.validateFormField('url'));
      },
    },

    isValidUrl() {
      if (!this.url) {
        return false;
      }

      try {
        const urlObj = new URL(this.url);
        return urlObj.protocol === 'https:';
      } catch {
        return false;
      }
    },

    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },

      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },

    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },

      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },

    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },

      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },

    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },

      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },

    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },

      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },

    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },

      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },

    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },

      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },

    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },

      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },

    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },

      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    border: {
      get() {
        return this.getPropertyValue('border');
      },

      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },

      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },

      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    blur: {
      get() {
        return this.getPropertyValue('blur');
      },

      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },

    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },

      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },
};
</script>
